/*
 * The bell's panel, full height on the right over a scrim that dims the
 * whole app (a click on the scrim closes the panel): what's coming due
 * (filing deadlines, expirations, appointments,
 * reminders) and what people did (mentions, activity, assignments and
 * intakes), in one flat list
 * grouped by when. Mine / Team, one Filter menu, and a pointer to Agents when
 * agents are waiting. Reminders can be snoozed or marked done in place. No
 * agent outcomes and no announcements: those have their own surfaces.
 */
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  Activity,
  AlarmClock,
  ArrowUpRight,
  AtSign,
  Building2,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock,
  FileClock,
  IdCard,
  ListFilter,
  Settings2,
  UserPlus,
  X,
  type LucideIcon
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Kbd } from "@/components/ui/kbd";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useAgentAttention } from "@/features/agentAttention/context/AgentAttentionProvider";
import { useAgentsTrayStore } from "@/features/agentAttention/context/agentsTrayStore";
import { itemSeed } from "@/features/agentAttention/faces";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { CALENDAR_PATH } from "@/features/calendar/routes";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";
import { cn } from "@/lib/utils";

import type { ComingUpItem, ComingUpScope } from "./comingUp/api";
import type { ComingUp } from "./comingUp/useComingUp";
import { useComingUp } from "./comingUp/useComingUp";
import {
  buildFeed,
  COMING_DUE_KINDS,
  dueGroup,
  duePill,
  FEED_KIND_LABELS,
  filterLabel,
  FROM_PEOPLE_KINDS,
  inboxKind,
  kindCounts,
  loadSelection,
  saveSelection,
  scopedSelection,
  type FeedKind,
  type FeedRow,
  type InboxKind
} from "./feedModel";
import { useNotifications } from "./NotificationsProvider";
import { rendererFor } from "./registry";
import { isUnread, type Notification } from "./types";

dayjs.extend(relativeTime);

const KIND_ICONS: Record<FeedKind, LucideIcon> = {
  filing: FileClock,
  expiration: IdCard,
  appointment: CalendarDays,
  reminder: Clock,
  mention: AtSign,
  activity: Activity,
  assignment: UserPlus,
  intake: ClipboardCheck,
  company: Building2
};

const REMINDER_PREFIX = "reminder:";
const BELL_SELECTOR = '[data-testid="notifications-bell"]';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";

/** A reminder's id, when the row is one you can snooze or mark done. */
export const actionableReminderId = (
  item: Pick<ComingUpItem, "id" | "kind" | "client_id">
): string | null =>
  item.kind === "reminder" &&
  item.client_id &&
  item.id.startsWith(REMINDER_PREFIX)
    ? item.id.slice(REMINDER_PREFIX.length)
    : null;

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.isContentEditable ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT"
  );
};

const rowClass =
  "group/row relative flex w-full cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors duration-100 hover:bg-fill-1 focus-visible:bg-fill-1 focus-visible:outline-none";

const RowButton: FC<{
  label: string;
  onClick: () => void;
  children: ReactNode;
}> = ({ label, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

export const DueRow: FC<{
  item: ComingUpItem;
  onOpen: (item: ComingUpItem) => void;
  onSnooze?: (item: ComingUpItem) => void;
  onDone?: (item: ComingUpItem) => void;
  onActive?: (item: ComingUpItem | null) => void;
}> = ({ item, onOpen, onSnooze, onDone, onActive }) => {
  const Icon = KIND_ICONS[item.kind];
  const late = dueGroup(item) === "overdue";
  const meta = [item.client_name, item.owner_name].filter(Boolean).join(" · ");
  const actions = Boolean(onSnooze && onDone);
  return (
    <div
      role="button"
      tabIndex={0}
      className={rowClass}
      data-testid="due-row"
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(item);
        }
      }}
      onMouseEnter={() => onActive?.(item)}
      onMouseLeave={() => onActive?.(null)}
      onFocus={() => onActive?.(item)}
    >
      <span title={FEED_KIND_LABELS[item.kind]} className="pt-px">
        <Icon className={cn("size-4", late ? "text-danger" : "text-fg-3")} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-base text-fg">{item.title}</span>
        {item.detail ? (
          <span className="line-clamp-2 text-sm text-fg-3">{item.detail}</span>
        ) : null}
        {meta ? (
          <span className="flex min-w-0 items-center gap-1.5 text-sm text-fg-3">
            {item.client_name ? (
              <Avatar size="2xs" className="size-3.5">
                <AvatarFallback>{initialsOf(item.client_name)}</AvatarFallback>
              </Avatar>
            ) : null}
            <span className="truncate">{meta}</span>
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "shrink-0",
          actions && "group-hover/row:hidden group-focus-within/row:hidden"
        )}
      >
        {late ? (
          <StatusBadge tone="danger">{duePill(item)}</StatusBadge>
        ) : (
          <span className="text-sm whitespace-nowrap text-fg-2">
            {duePill(item)}
          </span>
        )}
      </span>
      {actions ? (
        <span className="hidden shrink-0 items-start group-hover/row:flex group-focus-within/row:flex">
          <RowButton
            label="Snooze Until Tomorrow"
            onClick={() => onSnooze?.(item)}
          >
            <AlarmClock />
          </RowButton>
          <RowButton label="Mark Done" onClick={() => onDone?.(item)}>
            <Check />
          </RowButton>
        </span>
      ) : null}
    </div>
  );
};

export const UpdateRow: FC<{
  notification: Notification;
  kind: InboxKind;
  onOpen: (n: Notification) => void;
}> = ({ notification: n, kind, onOpen }) => {
  const renderer = rendererFor(n.type);
  const { title, description, avatar, action } = renderer.render(n);
  const Glyph = renderer.glyph ?? KIND_ICONS[kind];
  const unread = isUnread(n);
  return (
    <div
      role="button"
      tabIndex={0}
      className={rowClass}
      data-testid="update-row"
      data-unread={unread ? "true" : "false"}
      onClick={() => onOpen(n)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(n);
        }
      }}
    >
      <span className="relative h-5 shrink-0">
        <Avatar size="xs">
          {avatar?.url ? <AvatarImage src={avatar.url} alt="" /> : null}
          <AvatarFallback>
            {avatar ? initialsOf(avatar.name) : "CB"}
          </AvatarFallback>
        </Avatar>
        <span
          title={FEED_KIND_LABELS[kind]}
          className="absolute -right-1 -bottom-1 flex size-3 items-center justify-center rounded-full bg-elevated"
        >
          <Glyph className="size-2 text-fg-3" />
        </span>
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-start gap-1.5">
          <span
            className={cn(
              "min-w-0 flex-1 text-base text-fg",
              unread && "font-medium"
            )}
          >
            {title}
          </span>
          <span className="shrink-0 text-sm text-fg-3">
            {n.created_at ? dayjs(n.created_at).fromNow(true) : ""}
          </span>
          <span
            aria-hidden
            className={cn(
              "mt-1.5 size-1.5 shrink-0 rounded-full",
              unread ? "bg-primary" : "bg-transparent"
            )}
          />
        </span>
        {description ? (
          <span className="line-clamp-2 text-sm text-fg-3">{description}</span>
        ) : null}
        {action && n.link ? (
          <span className="mt-1 flex">
            <Button
              size="xs"
              data-testid="update-row-action"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(n);
              }}
            >
              {action}
            </Button>
          </span>
        ) : null}
      </span>
    </div>
  );
};

const Section: FC<{
  id: string;
  title: string;
  count: number;
  children: ReactNode;
}> = ({ id, title, count, children }) => (
  <section className="flex flex-col" data-testid={`feed-${id}`}>
    <div className="flex h-7 items-center gap-1.5 px-2.5 text-sm text-fg-3">
      {title}
      <span className="text-fg-4 tabular-nums">{count}</span>
    </div>
    {children}
  </section>
);

const HeaderIcon: FC<{
  label: string;
  onClick: () => void;
  children: ReactNode;
  testId?: string;
}> = ({ label, onClick, children, testId }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        data-testid={testId}
        onClick={onClick}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

const AgentsPointer: FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { items } = useAgentAttention();
  if (!items.length) return null;
  return (
    <div
      data-testid="agents-pointer"
      className="mx-3 mt-1 flex items-center gap-2.5 rounded-lg py-1.5 pr-1.5 pl-2.5 ring-1 ring-line-2"
    >
      <AgentMoodAvatar
        className="size-5 rounded-full"
        agent={{ key: itemSeed(items[0]) }}
      />
      <span className="min-w-0 flex-1 truncate text-base text-fg">
        {items.length === 1
          ? "1 agent update is"
          : `${items.length} agent updates are`}{" "}
        waiting in Agents
      </span>
      <Button variant="ghost" size="xs" onClick={onOpen}>
        View
        <ChevronRight data-icon="inline-end" />
      </Button>
    </div>
  );
};

const FilterMenu: FC<{
  scope: ComingUpScope;
  selection: FeedKind[];
  counts: Record<FeedKind, number>;
  onChange: (next: FeedKind[]) => void;
}> = ({ scope, selection, counts, onChange }) => {
  const active = selection.length > 0;
  const toggle = (k: FeedKind) =>
    onChange(
      selection.includes(k)
        ? selection.filter((x) => x !== k)
        : [...selection, k]
    );
  const sections: [string, FeedKind[]][] = [["Coming Due", COMING_DUE_KINDS]];
  if (scope === "mine") sections.push(["From People", FROM_PEOPLE_KINDS]);
  return (
    <DropdownMenu>
      <div className="relative inline-flex">
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="xs"
            data-testid="notifications-filter"
            className={cn(active && "bg-fill-2 pr-6 text-fg")}
          >
            <ListFilter className={cn(active && "text-primary")} />
            {filterLabel(selection)}
            {active ? null : <ChevronDown className="size-3" />}
          </Button>
        </DropdownMenuTrigger>
        {active ? (
          <button
            type="button"
            aria-label="Clear filter"
            data-testid="notifications-filter-clear"
            onClick={() => onChange([])}
            className="absolute top-1/2 right-1 flex size-4 -translate-y-1/2 items-center justify-center rounded-xs text-fg-3 hover:bg-fill-3 hover:text-fg"
          >
            <X className="size-3" />
          </button>
        ) : null}
      </div>
      <DropdownMenuContent align="end" className="w-56">
        {sections.map(([title, kinds], i) => (
          <div key={title}>
            {i ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="text-sm font-normal text-fg-3">
              {title}
            </DropdownMenuLabel>
            {kinds.map((k) => {
              const Icon = KIND_ICONS[k];
              return (
                <DropdownMenuCheckboxItem
                  key={k}
                  checked={selection.includes(k)}
                  onCheckedChange={() => toggle(k)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <Icon className="size-3.5 text-fg-3" />
                  {FEED_KIND_LABELS[k]}
                  <span className="ml-auto text-sm text-fg-3 tabular-nums">
                    {counts[k]}
                  </span>
                </DropdownMenuCheckboxItem>
              );
            })}
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!active} onSelect={() => onChange([])}>
          Show Everything
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const tomorrowMorning = () =>
  dayjs().add(1, "day").hour(9).minute(0).second(0).millisecond(0);

export const NotificationsSheet: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** My coming-up items, kept fresh by the bell for its count. */
  mine: ComingUp;
  onOpenPreferences: () => void;
}> = ({ open, onOpenChange, mine, onOpenPreferences }) => {
  const navigate = useNavigate();
  const {
    userDetails: { id: uid }
  } = useGetUserDetails();
  const { items: inbox, markRead, markAllRead } = useNotifications();
  const openAgents = useAgentsTrayStore((s) => s.setOpen);
  const [scope, setScope] = useState<ComingUpScope>("mine");
  const [selection, setSelection] = useState<FeedKind[]>(() =>
    loadSelection(uid)
  );
  const team = useComingUp("team", open && scope === "team");
  const [cleared, setCleared] = useState<Set<string>>(() => new Set());
  const [active, setActive] = useState<ComingUpItem | null>(null);

  useEffect(() => setSelection(loadSelection(uid)), [uid]);
  useEffect(() => {
    if (open) void mine.refresh();
    // Refresh once per open, not on every poll result.
  }, [open]);

  const changeSelection = (next: FeedKind[]) => {
    setSelection(next);
    saveSelection(uid, next);
  };
  const changeScope = (next: ComingUpScope) => {
    setScope(next);
    if (next === "team") changeSelection(scopedSelection(selection, "team"));
  };

  const due = useMemo(
    () =>
      (scope === "mine" ? mine.items : team.items).filter(
        (i) => !cleared.has(i.id)
      ),
    [scope, mine.items, team.items, cleared]
  );
  const loading = scope === "mine" ? mine.loading : team.loading;
  const shownInbox = useMemo(
    () => inbox.filter((n) => inboxKind(n) !== null),
    [inbox]
  );
  const sections = useMemo(
    () => buildFeed({ due, inbox: shownInbox, scope, selection }),
    [due, shownInbox, scope, selection]
  );
  const counts = useMemo(
    () => kindCounts(due, shownInbox, scope),
    [due, shownInbox, scope]
  );
  const unread = shownInbox.some(isUnread);
  const canAct = sections.some((s) =>
    s.rows.some((r) => r.kind === "due" && actionableReminderId(r.item))
  );

  const close = () => onOpenChange(false);
  const openDue = (item: ComingUpItem) => {
    close();
    const to =
      item.path || (item.client_id ? `/clients/${item.client_id}` : null);
    if (to) navigate(to);
  };
  const openUpdate = (n: Notification) => {
    if (isUnread(n)) void markRead([n.id]);
    close();
    if (n.link) navigate(n.link);
  };

  const { refresh: refreshMine } = mine;
  const { refresh: refreshTeam } = team;
  const updateReminder = useCallback(
    async (
      item: ComingUpItem,
      patch: Parameters<typeof ForeignNationalsAPI.updateReminder>[2],
      done: string
    ) => {
      const id = actionableReminderId(item);
      if (!id || !item.client_id) return;
      setCleared((prev) => new Set(prev).add(item.id));
      setActive(null);
      try {
        await ForeignNationalsAPI.updateReminder(item.client_id, id, patch);
        toast.success(done);
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Couldn't update the reminder"
        );
      } finally {
        await Promise.all([refreshMine(), refreshTeam()]);
        setCleared((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [refreshMine, refreshTeam]
  );
  const snooze = useCallback(
    (item: ComingUpItem) =>
      void updateReminder(
        item,
        { due_date: tomorrowMorning().toISOString() },
        "Snoozed until tomorrow"
      ),
    [updateReminder]
  );
  const markDone = useCallback(
    (item: ComingUpItem) =>
      void updateReminder(
        item,
        { status: "COMPLETED", completed_at: new Date().toISOString() },
        "Reminder marked done"
      ),
    [updateReminder]
  );

  useEffect(() => {
    if (!open || !active || !actionableReminderId(active)) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const key = e.key.toLowerCase();
      if (key === "h") snooze(active);
      else if (key === "e") markDone(active);
      else return;
      e.preventDefault();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, active, snooze, markDone]);

  const row = (r: FeedRow) => {
    if (r.kind === "inbox")
      return (
        <UpdateRow
          key={r.item.id}
          notification={r.item}
          kind={r.inbox}
          onOpen={openUpdate}
        />
      );
    const actionable = Boolean(actionableReminderId(r.item));
    return (
      <DueRow
        key={r.item.id}
        item={r.item}
        onOpen={openDue}
        onSnooze={actionable ? snooze : undefined}
        onDone={actionable ? markDone : undefined}
        onActive={setActive}
      />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      {open
        ? createPortal(
            <div
              aria-hidden
              data-testid="notifications-scrim"
              className="fixed inset-0 z-39 animate-in bg-black/40 duration-100 fade-in-0"
            />,
            document.body
          )
        : null}
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          const target = e.target as Element | null;
          if (target?.closest?.(BELL_SELECTOR)) e.preventDefault();
        }}
        className="z-40 w-105 gap-0 border-l border-line-3 bg-elevated p-0 shadow-none sm:max-w-105"
        data-testid="notifications-sheet"
      >
        <div className="flex h-11 shrink-0 items-center gap-1.5 pr-2 pl-4">
          <SheetTitle className="shrink-0 text-base font-medium text-fg">
            Notifications
          </SheetTitle>
          <SheetDescription className="min-w-0 flex-1 truncate text-sm text-fg-3">
            What's coming due and what people did
          </SheetDescription>
          {unread ? (
            <HeaderIcon
              label="Mark All Read"
              testId="notifications-mark-all"
              onClick={() => void markAllRead()}
            >
              <CheckCheck />
            </HeaderIcon>
          ) : null}
          <HeaderIcon
            label="Notification Settings"
            testId="notifications-preferences-link"
            onClick={() => {
              close();
              onOpenPreferences();
            }}
          >
            <Settings2 />
          </HeaderIcon>
          <HeaderIcon label="Close" onClick={close}>
            <X />
          </HeaderIcon>
        </div>

        <div className="flex h-9 shrink-0 items-center gap-1.5 pr-3 pl-4">
          <Tabs
            value={scope}
            onValueChange={(v) => changeScope(v === "team" ? "team" : "mine")}
          >
            <TabsList aria-label="Whose items">
              <TabsTrigger value="mine">Mine</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>
          </Tabs>
          <span className="flex-1" />
          <FilterMenu
            scope={scope}
            selection={scopedSelection(selection, scope)}
            counts={counts}
            onChange={changeSelection}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pt-1 pb-2">
          <AgentsPointer
            onOpen={() => {
              close();
              openAgents(true);
            }}
          />

          {sections.length ? (
            <div className="flex flex-col gap-1.5 px-1.5">
              {sections.map((s) => (
                <Section
                  key={s.group}
                  id={s.group}
                  title={s.label}
                  count={s.rows.length}
                >
                  {s.rows.map(row)}
                </Section>
              ))}
            </div>
          ) : (
            <p
              className="px-4 py-3 text-sm text-fg-3"
              data-testid="notifications-empty"
            >
              {loading ? (
                "Loading…"
              ) : scopedSelection(selection, scope).length ? (
                <>
                  Nothing matches this filter.{" "}
                  <Button
                    variant="link"
                    className="text-sm"
                    onClick={() => changeSelection([])}
                  >
                    Show Everything
                  </Button>
                </>
              ) : (
                "Nothing here."
              )}
            </p>
          )}
        </div>

        <div className="flex h-10 shrink-0 items-center gap-3 border-t border-line-2 pr-2 pl-4 text-sm text-fg-3">
          {canAct ? (
            <>
              <span className="flex items-center gap-1.5">
                <Kbd>H</Kbd>
                Snooze
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>E</Kbd>
                Done
              </span>
            </>
          ) : null}
          <Button
            variant="ghost"
            size="xs"
            className="ml-auto"
            onClick={() => {
              close();
              navigate(CALENDAR_PATH);
            }}
          >
            Open Calendar
            <ArrowUpRight data-icon="inline-end" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
